import { Account, Contact, Customer, Employee, FleetMasterContract, UserContext } from '@hdms/shared/common-types';

/**
 * Create Contact or Account fallback just containing a typename property
 * @param typeName
 */
const getContactAccountFallback = <T = Account | Contact>(typeName: Account['__typename'] | Contact['__typename']): T =>
  ({
    id: null,
    originId: null,
    __typename: typeName
  } as T);

export const isEmployee = (context: Customer | Employee): context is Employee => context.__typename === 'Employee';

/**
 * In case of an Employee, return `customerContact` if existing, else fallback to return just the email as formatted name.
 * Regular employees (not AMs) won't have any contact information, so in case they would log in,
 * there's no information available to be displayed in user account button in header.
 * Using this fallback, at least employee's email address will be shown.
 * @private
 */
const getEmployeeContact = (context: UserContext): Contact =>
  isEmployee(context) && (context.customerContact || ({ formatted: [context.email] } as Contact));

export const getAccount = (context: Customer | Employee): Account => {
  context ??= {} as Customer | Employee;
  return (
    (context as Customer).companyAccount ||
    (context as Employee).customerCompanyAccount ||
    getContactAccountFallback('Account')
  );
};

export const getContact = (context: Customer | Employee): Contact => {
  context ??= {} as Customer | Employee;
  return (context as Customer).contact || getEmployeeContact(context) || getContactAccountFallback('Contact');
};

export const getFleetMasterContact = (context: Customer | Employee): FleetMasterContract => {
  context ??= {} as Customer | Employee;
  return (context as Customer).fleetMasterContract || (context as Employee).customerFleetMasterContract;
};
